import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../contexts/AuthContext';
import { getStudentMe, resolveStudentGrade, type RosterStudent } from '../api/roster.client';
import { ROSTER_STUDENT_ME_QUERY_KEY } from '../queryKeys';

const STALE_TIME_MS = 5 * 60 * 1000;

/**
 * Cached roster student profile for the current session.
 * Uses React Query: refetches after staleTime; cleared on logout (see AuthContext).
 */
export function useStudentMe() {
  const { loading: authLoading, isAuthenticated } = useAuth();
  const enabled = !authLoading && isAuthenticated;

  const query = useQuery<RosterStudent, Error>({
    queryKey: ROSTER_STUDENT_ME_QUERY_KEY,
    queryFn: getStudentMe,
    staleTime: STALE_TIME_MS,
    enabled,
  });

  const student = query.data;
  const grade = useMemo(() => (student ? resolveStudentGrade(student) : null), [student]);

  /** True while resolving auth or the first active fetch of students/me. */
  const isRosterLoading = authLoading || (enabled && query.isLoading);

  return {
    ...query,
    authLoading,
    enabled,
    student,
    grade,
    isRosterLoading,
  };
}
